import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Page, PageHeader } from "@/components/Bits";
import { WarningCircle, Warning, Info, CheckCircle, ArrowRight, ArrowsClockwise } from "@phosphor-icons/react";

const SEV = {
  high: { label: "High", ring: "border-l-negative", icon: WarningCircle, color: "text-negative", chip: "bg-negative/10 text-negative" },
  medium: { label: "Watch", ring: "border-l-[#F37A24]", icon: Warning, color: "text-[#B45309]", chip: "bg-orange-100 text-[#B45309]" },
  info: { label: "Info", ring: "border-l-orange", icon: Info, color: "text-orangeink", chip: "bg-orange/10 text-orangeink" },
};

export default function Insights() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/insights");
      setData(data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const insights = data?.insights || [];
  const counts = data?.counts || {};

  return (
    <Page>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <PageHeader overline="Anomalies & insights" title="Insights"
          subtitle="Automated checks across your active organisation — variances, missing recurring entries and balance movements." />
        <button onClick={load} data-testid="insights-refresh-btn"
          className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5 transition-colors">
          <ArrowsClockwise size={16} /> Refresh
        </button>
      </div>

      <div className="flex items-center gap-3 mb-6 flex-wrap" data-testid="insights-counts">
        {["high", "medium", "info"].map((k) => (
          <div key={k} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium ${SEV[k].chip}`}>
            {SEV[k].label}: {counts[k] || 0}
          </div>
        ))}
      </div>

      {loading ? (
        <div className="text-sm font-mono text-slate-600 py-10 tracking-widest">LOADING…</div>
      ) : insights.length === 0 ? (
        <div className="card-surface p-14 text-center" data-testid="insights-all-clear">
          <div className="w-14 h-14 rounded-2xl bg-positive/10 flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={30} weight="fill" className="text-positive" />
          </div>
          <div className="font-heading font-bold text-xl text-navy">All clear</div>
          <div className="text-sm text-slate-600 mt-1.5">No anomalies detected for this organisation right now.</div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="insights-list">
          {insights.map((i, idx) => {
            const sev = SEV[i.severity] || SEV.info;
            const Icon = sev.icon;
            return (
              <motion.div key={i.id} data-testid={`insight-${i.id}`}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: idx * 0.04 }}
                className={`card-surface border-l-4 ${sev.ring} p-5 flex items-start gap-4`}>
                <Icon size={22} weight="fill" className={`${sev.color} shrink-0 mt-0.5`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-navy text-sm">{i.title}</span>
                    <span className={`text-[0.62rem] font-semibold uppercase tracking-wider px-2.5 py-0.5 rounded-full ${sev.chip}`}>{sev.label}</span>
                  </div>
                  <div className="text-sm text-slate-600 mt-1">{i.message}</div>
                </div>
                {i.link && (
                  <Link to={i.link} data-testid={`insight-link-${i.id}`}
                    className="shrink-0 flex items-center gap-1 text-xs font-semibold text-orangeink hover:gap-1.5 transition-all mt-1">
                    {i.link_label || "Open"} <ArrowRight size={12} />
                  </Link>
                )}
              </motion.div>
            );
          })}
        </div>
      )}
    </Page>
  );
}
